// components/ErrorBoundary.js
//
// Vangnet tegen een "wit scherm": zonder error boundary haalt React bij elke
// onverwachte fout in één onderdeel de HELE pagina weg (menu, tabbalk, alles),
// zodat je niet meer terug kan. Met deze boundary blijft de rest staan en zie
// je een melding met "Opnieuw proberen" (en "Pagina herladen").
//
//  - resetKey: verandert deze waarde (bv. een andere tab of route), dan wordt
//    de fout automatisch gewist en wordt de inhoud opnieuw getoond.
//  - compact: kleinere melding voor een deel van de pagina (tab-inhoud).
import React from 'react';
import i18n from '../i18n';

const TEXT = {
  nl: { title: 'Er ging iets mis', body: 'Dit onderdeel kon niet worden getoond.', retry: 'Opnieuw proberen', reload: 'Pagina herladen' },
  en: { title: 'Something went wrong', body: 'This part could not be displayed.', retry: 'Try again', reload: 'Reload page' },
  fr: { title: 'Une erreur est survenue', body: 'Cette partie n’a pas pu être affichée.', retry: 'Réessayer', reload: 'Recharger la page' },
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary:', error, info && info.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;

    const lang = ((i18n && (i18n.resolvedLanguage || i18n.language)) || 'nl').slice(0, 2);
    const tx = TEXT[lang] || TEXT.nl;
    const compact = !!this.props.compact;

    return (
      <div
        role="alert"
        style={{
          boxSizing: 'border-box',
          margin: compact ? '24px auto' : '0 auto',
          minHeight: compact ? 0 : '60vh',
          maxWidth: 420,
          padding: compact ? '24px 20px' : '48px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          textAlign: 'center',
          fontFamily: 'var(--ss-font-body, system-ui, sans-serif)',
          color: 'var(--ss-text, #1f2937)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: 'var(--ss-ink, #12284a)' }}>{tx.title}</h2>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--ss-text-soft, #6b7280)' }}>{tx.body}</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 }}>
          <button
            type="button"
            onClick={this.reset}
            style={{ border: 'none', borderRadius: 999, padding: '10px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer', background: 'var(--ss-ink, #12284a)', color: '#fff' }}
          >
            {tx.retry}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ border: '1px solid var(--ss-line, #e3dccf)', borderRadius: 999, padding: '10px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer', background: '#fff', color: 'var(--ss-ink, #12284a)' }}
          >
            {tx.reload}
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
